import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ChevronDown, X, Search } from 'lucide-react';
import './MultiSelectFilter.css';

type OptionItem = string | { label: string; value: string };

interface MultiSelectFilterProps {
    label: string | React.ReactNode;
    options: OptionItem[];
    selectedValues: string[]; // always values
    onSelectionChange: (values: string[]) => void;
    placeholder?: string;
    searchPlaceholder?: string;
    loading?: boolean;
}

const MultiSelectFilter: React.FC<MultiSelectFilterProps> = ({
    label,
    options,
    selectedValues,
    onSelectionChange,
    placeholder = "すべて選択",
    searchPlaceholder = "検索...",
    loading = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Filter options based on search term using useMemo to prevent infinite re-renders
    const normalized = useMemo(() => {
        return options.map(opt => typeof opt === 'string' ? ({ label: opt, value: opt }) : opt);
    }, [options]);

    const valueToLabel = useMemo(() => {
        const map = new Map<string, string>();
        normalized.forEach(o => map.set(o.value, o.label));
        return map;
    }, [normalized]);

    const filteredOptions = useMemo(() => {
        if (searchTerm) {
            return normalized.filter(option =>
                option.label.toLowerCase().includes(searchTerm.toLowerCase())
            );
        } else {
            return normalized;
        }
    }, [searchTerm, normalized]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleToggleOption = (optionValue: string) => {
        if (selectedValues.includes(optionValue)) {
            onSelectionChange(selectedValues.filter(value => value !== optionValue));
        } else {
            onSelectionChange([...selectedValues, optionValue]);
        }
    };

    const handleSelectAll = () => {
        if (selectedValues.length === filteredOptions.length) {
            onSelectionChange([]);
        } else {
            onSelectionChange(filteredOptions.map(o => o.value));
        }
    };

    const handleRemoveValue = (value: string) => {
        onSelectionChange(selectedValues.filter(v => v !== value));
    };

    const handleClearAll = () => {
        onSelectionChange([]);
    };

    return (
        <div className="multi-select-filter" ref={dropdownRef}>
            <label className="filter-label">{label}</label>
            <div className="multi-select-container">
                <div
                    className={`multi-select-trigger ${isOpen ? 'open' : ''}`}
                    onClick={() => setIsOpen(!isOpen)}
                >
                    <div className="selected-values">
                        {selectedValues.length === 0 ? (
                            <span className="placeholder">{placeholder}</span>
                        ) : (
                            <div className="selected-tags">
                                {selectedValues.slice(0, 2).map(value => (
                                    <span key={value} className="selected-tag">
                                        {valueToLabel.get(value) || value}
                                        <button
                                            type="button"
                                            className="remove-tag"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemoveValue(value);
                                            }}
                                        >
                                            <X size={12} />
                                        </button>
                                    </span>
                                ))}
                                {selectedValues.length > 2 && (
                                    <span className="more-count">+{selectedValues.length - 2}</span>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="multi-select-actions">
                        {selectedValues.length > 0 && (
                            <button
                                type="button"
                                className="clear-all"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleClearAll();
                                }}
                            >
                                <X size={16} />
                            </button>
                        )}
                        <ChevronDown size={16} className={`chevron ${isOpen ? 'open' : ''}`} />
                    </div>
                </div>

                {isOpen && (
                    <div className="multi-select-dropdown">
                        <div className="search-container">
                            <div className="search-icon-container">
                                <Search size={20} className="search-icon" />
                                <input
                                    type="text"
                                    placeholder={searchPlaceholder}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="search-input"
                                    autoFocus
                                />
                            </div>
                        </div>

                        {loading ? (
                            <div className="loading-options">読み込み中...</div>
                        ) : (
                            <>
                                <div className="select-all-container">
                                    <button
                                        type="button"
                                        className="select-all-btn"
                                        onClick={handleSelectAll}
                                    >
                                        {selectedValues.length === filteredOptions.length ? 'すべて解除' : 'すべて選択'}
                                    </button>
                                </div>

                                <div className="options-list">
                                    {filteredOptions.length === 0 ? (
                                        <div className="no-options">該当する項目がありません</div>
                                    ) : (
                                        filteredOptions.map(option => (
                                            <div
                                                key={option.value}
                                                className={`option ${selectedValues.includes(option.value) ? 'selected' : ''}`}
                                                onClick={() => handleToggleOption(option.value)}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedValues.includes(option.value)}
                                                    onChange={() => { }} // Handled by onClick
                                                    className="option-checkbox"
                                                />
                                                <span className="option-text">{option.label}</span>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MultiSelectFilter;
